import { computed, reactive, ref, type Ref } from 'vue';
import { useVuelidate, type ValidationArgs } from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import { PaymentProviderType } from '@/types/composition-functions';
import type {
  PaymentProvider,
  PaymentProviderInput,
  PaymentProviderStatus,
} from '@/types/composition-functions';
import { humanizeCode, isCode, normalizeCode } from '@/utils/billingLabels';
import { firstValidationMessage, toUserMessage } from '@/utils/error';
import { useFormErrors } from '@/composables/useFormErrors';

/**
 * Logic behind Settings → Payment: provider names, the key fields each
 * provider needs (with help text a bursar can follow), validation, the setup
 * status list and the warnings shown before switching a provider on or off.
 *
 * Provider names are compared through normalizeCode so 'PAYSTACK', 'Paystack'
 * and 'paystack' all match.
 */

/** A provider row as GET_SCHOOL_QUERY returns it. */
export type SavedProvider = Pick<PaymentProvider, 'id' | 'name' | 'is_active' | 'config'>;

/** Providers a school can add from this screen. */
export const ADDABLE_PROVIDERS: readonly PaymentProviderType[] = [
  PaymentProviderType.Monnify,
  PaymentProviderType.Paystack,
  PaymentProviderType.Remita,
  PaymentProviderType.Interswitch,
];

const PROVIDER_LABELS: Record<string, string> = {
  monnify: 'Monnify',
  paystack: 'Paystack',
  remita: 'Remita',
  interswitch: 'Interswitch',
  flutterwave: 'Flutterwave',
};

const PROVIDER_DESCRIPTIONS: Record<string, string> = {
  monnify: 'Bank transfer and card payments',
  paystack: 'Card, bank transfer, USSD and more',
  remita: 'Payments through Remita',
  interswitch: 'Card and bank payments through Interswitch',
  flutterwave: 'Card, bank transfer and mobile money',
};

/** 'PAYSTACK' / 'Paystack' -> 'paystack'. */
export function providerKey(name: string | null | undefined): string {
  return normalizeCode(name);
}

export function providerLabel(name: string | null | undefined): string {
  if (!name) return 'Payment provider';
  return PROVIDER_LABELS[providerKey(name)] ?? humanizeCode(name);
}

export function providerDescription(name: string | null | undefined): string {
  return PROVIDER_DESCRIPTIONS[providerKey(name)] ?? 'Online payments';
}

/** "a, b and c" — for lists of missing fields in sentences. */
export function joinWithAnd(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

// ── Key fields per provider ──

// @allow-handwritten UI-only field descriptor for the provider setup form; never crosses the wire
export interface ProviderFormField {
  key: string;
  label: string;
  /** One line telling a bursar where to find the value. */
  help: string;
  placeholder: string;
  /** Needed before the provider can take payments. */
  required: boolean;
  /** Never shown again once saved; typed into a password box. */
  secret: boolean;
}

const KEPT_PRIVATE = 'Kept private — it is never shown again after you save.';

export const PROVIDER_FORM_FIELDS: Readonly<Record<string, ProviderFormField[]>> = {
  monnify: [
    {
      key: 'apiKey',
      label: 'API Key',
      help: 'In your Monnify dashboard open Settings → API Keys & Webhooks and copy the API Key.',
      placeholder: 'Paste your Monnify API Key',
      required: true,
      secret: false,
    },
    {
      key: 'contractCode',
      label: 'Contract Code',
      help: 'On the same Monnify page. It tells Monnify which of your accounts receives the money.',
      placeholder: 'Paste your Contract Code',
      required: true,
      secret: false,
    },
    {
      key: 'secretKey',
      label: 'Secret Key',
      help: `On the same Monnify page. ${KEPT_PRIVATE}`,
      placeholder: 'Paste your Monnify Secret Key',
      required: true,
      secret: true,
    },
  ],
  paystack: [
    {
      key: 'publicKey',
      label: 'Public Key',
      help: 'In your Paystack dashboard open Settings → API Keys & Webhooks. It starts with pk_live_ (or pk_test_ while testing).',
      placeholder: 'pk_live_…',
      required: true,
      secret: false,
    },
    {
      key: 'secretKey',
      label: 'Secret Key',
      help: `On the same Paystack page. It starts with sk_live_ (or sk_test_). ${KEPT_PRIVATE}`,
      placeholder: 'sk_live_…',
      required: true,
      secret: true,
    },
  ],
  remita: [
    {
      key: 'apiKey',
      label: 'API Key',
      help: `From the integration (API) settings of your Remita account. ${KEPT_PRIVATE}`,
      placeholder: 'Paste your Remita API Key',
      required: true,
      secret: true,
    },
    {
      key: 'merchantId',
      label: 'Merchant ID',
      help: 'Your Remita merchant number, shown on your Remita profile.',
      placeholder: 'e.g. 2547916',
      required: true,
      secret: false,
    },
    {
      key: 'serviceTypeId',
      label: 'Service Type ID',
      help: 'The ID of the school-fees service you created on Remita. Remita uses it to know what the payment is for.',
      placeholder: 'e.g. 4430731',
      required: true,
      secret: false,
    },
  ],
};

export function formFieldsFor(name: string | null | undefined): ProviderFormField[] {
  return PROVIDER_FORM_FIELDS[providerKey(name)] ?? [];
}

/** Field keys the server reports as already saved for a provider. */
export function savedFieldKeys(status: PaymentProviderStatus | null | undefined): string[] {
  return (status?.fields ?? []).filter((field) => field.present).map((field) => field.key);
}

/**
 * A secret already saved can be left blank on edit — the server keeps it —
 * so it only stops the form when nothing is stored yet.
 */
export function isFieldRequired(field: ProviderFormField, savedKeys: string[], editing: boolean): boolean {
  if (!field.required) return false;
  return !(field.secret && editing && savedKeys.includes(field.key));
}

export function fieldPlaceholder(field: ProviderFormField, saved: boolean): string {
  return field.secret && saved ? 'Saved — leave blank to keep the current key' : field.placeholder;
}

const PAYSTACK_KEY_PREFIX: Record<string, { prefix: string; message: string }> = {
  publicKey: {
    prefix: 'pk_',
    message: 'A Paystack public key starts with pk_live_ (or pk_test_). Check you have not pasted the secret key here.',
  },
  secretKey: {
    prefix: 'sk_',
    message: 'A Paystack secret key starts with sk_live_ (or sk_test_). Check you have not pasted the public key here.',
  },
};

/** Catches the classic mix-up of pasting Paystack's public and secret keys into each other's box. */
export function keyFormatProblem(providerName: string, key: string, value: string | null | undefined): string {
  const trimmed = (value ?? '').trim();
  if (!trimmed || !isCode(providerName, PaymentProviderType.Paystack)) return '';
  const rule = PAYSTACK_KEY_PREFIX[key];
  if (!rule || trimmed.startsWith(rule.prefix)) return '';
  return rule.message;
}

function providerType(name: string): PaymentProviderType {
  return ADDABLE_PROVIDERS.find((type) => isCode(name, type)) ?? (name as PaymentProviderType);
}

/** The setPaymentProvider input for the chosen provider. A blank secret keeps the saved one. */
export function buildProviderInput(name: string, values: Record<string, string>): PaymentProviderInput {
  const value = (key: string) => (values[key] ?? '').trim();
  const input: PaymentProviderInput = { name: providerType(name) };
  switch (providerKey(name)) {
  case 'monnify':
    input.monnify = { apiKey: value('apiKey'), contractCode: value('contractCode'), secretKey: value('secretKey') };
    break;
  case 'paystack':
    input.paystack = { publicKey: value('publicKey'), secretKey: value('secretKey') };
    break;
  case 'remita':
    input.remita = { apiKey: value('apiKey'), merchantId: value('merchantId'), serviceTypeId: value('serviceTypeId') };
    break;
  default:
    break;
  }
  return input;
}

// @allow-handwritten UI-only dropdown option; never crosses the wire
export interface ProviderOption {
  label: string;
  value: PaymentProviderType;
  disabled: boolean;
}

/**
 * Adding a provider the school already has would overwrite its saved keys
 * (the server saves by provider name), so those are disabled in add mode.
 */
export function providerOptionsFor(existingNames: Array<string | null | undefined>, editing: boolean): ProviderOption[] {
  return ADDABLE_PROVIDERS.map((value) => {
    const added = existingNames.some((name) => isCode(name, value));
    const label = providerLabel(value);
    return {
      value,
      label: added && !editing ? `${label} — already added (use its pencil button to change it)` : label,
      disabled: added && !editing,
    };
  });
}

/** Toast detail when saving a provider fails: field problems are shown on the form itself. */
export function providerSaveErrorDetail(error: unknown): string {
  if (firstValidationMessage(error)) return 'Some details need fixing — see the highlighted fields.';
  return toUserMessage(error, 'Your changes were not saved. Please try again.');
}

// ── Setup status ──

export type ProviderSetupState = 'ready' | 'needs_setup' | 'off' | 'checking';

// @allow-handwritten UI-only status row for the setup card; never crosses the wire
export interface ProviderSetupRow {
  id: string | null;
  name: string;
  label: string;
  state: ProviderSetupState;
  badge: string;
  severity: 'success' | 'warn' | 'secondary' | 'info';
  summary: string;
  /** Plain names of what is still needed, e.g. ['Secret Key']. */
  missing: string[];
  isActive: boolean;
}

function missingLabels(status: PaymentProviderStatus): string[] {
  return status.missingFields.map((key) => status.fields.find((field) => field.key === key)?.label ?? humanizeCode(key));
}

/**
 * One row of the setup card for a provider the school has added.
 * Interswitch keeps its details on PAY Items, so it is ready once at least
 * one active PAY Item exists. payItemCount: a number once loaded, null while
 * loading, undefined when this user can't see PAY Items.
 */
export function providerSetupRow(
  provider: SavedProvider,
  status: PaymentProviderStatus | null | undefined,
  payItemCount: number | null | undefined,
): ProviderSetupRow {
  const name = provider.name ?? '';
  const label = providerLabel(name);
  const isActive = provider.is_active === true;
  const base = { id: provider.id ?? null, name, label, isActive };

  let missing: string[];
  let checking = false;
  if (isCode(name, PaymentProviderType.Interswitch)) {
    if (payItemCount === undefined) {
      return {
        ...base,
        state: isActive ? 'checking' : 'off',
        badge: isActive ? 'On' : 'Off',
        severity: isActive ? 'info' : 'secondary',
        summary: 'Interswitch takes payments through its PAY Items. Ask an administrator who can manage PAY Items to check them.',
        missing: [],
      };
    }
    checking = payItemCount === null;
    missing = payItemCount ? [] : ['a PAY Item'];
  } else if (status) {
    missing = missingLabels(status);
    if (!status.isConfigured && !missing.length) missing = ['its keys'];
  } else {
    checking = true;
    missing = [];
  }

  if (checking) {
    return { ...base, state: 'checking', badge: 'Checking…', severity: 'secondary', summary: 'Checking the setup…', missing };
  }

  const ready = missing.length === 0;
  if (!isActive) {
    return {
      ...base,
      state: 'off',
      badge: 'Off',
      severity: 'secondary',
      summary: ready
        ? `Switched off — students can't pay with ${label} until you switch it on below.`
        : `Switched off. Still needs ${joinWithAnd(missing)} before it can take payments.`,
      missing,
    };
  }
  if (ready) {
    const detail = isCode(name, PaymentProviderType.Interswitch)
      ? `${payItemCount} PAY Item${payItemCount === 1 ? '' : 's'} set up`
      : `students can pay with ${label}`;
    return { ...base, state: 'ready', badge: 'Ready', severity: 'success', summary: `Ready — ${detail}.`, missing };
  }
  return {
    ...base,
    state: 'needs_setup',
    badge: 'Needs setup',
    severity: 'warn',
    summary: `Switched on but not finished — still needs ${joinWithAnd(missing)}. Students' payments will fail until this is done.`,
    missing,
  };
}

/** Rows for every provider the school has added (not every provider the platform supports). */
export function providerSetupRows(
  providers: Array<SavedProvider | null | undefined>,
  statuses: PaymentProviderStatus[],
  interswitchPayItemCount: number | null | undefined,
): ProviderSetupRow[] {
  return providers
    .filter((provider): provider is SavedProvider => !!provider?.name)
    .map((provider) => providerSetupRow(
      provider,
      statuses.find((status) => isCode(status.name, provider.name ?? '')),
      interswitchPayItemCount,
    ));
}

// @allow-handwritten UI-only confirmation copy; never crosses the wire
export interface ProviderToggleWarning {
  header: string;
  message: string;
  acceptLabel: string;
}

/**
 * The consequence to confirm before a switch flips, or null when no warning
 * is needed: switching off the only provider that is on stops all online
 * payments, and switching on an unfinished one shows students a checkout
 * that fails.
 */
export function providerToggleWarning(row: ProviderSetupRow, turnOn: boolean, activeCount: number): ProviderToggleWarning | null {
  if (!turnOn && row.isActive && activeCount <= 1) {
    return {
      header: `Switch off ${row.label}?`,
      message: `${row.label} is the only payment provider switched on. If you switch it off, students and parents `
        + 'can\'t pay online until a provider is switched back on. Payments already made are not affected, and you can '
        + 'switch it back on at any time.',
      acceptLabel: 'Switch off',
    };
  }
  if (turnOn && row.missing.length) {
    return {
      header: `${row.label} isn't finished`,
      message: `${row.label} still needs ${joinWithAnd(row.missing)}. If you switch it on now, students will see it `
        + 'when they pay but their payments will fail. Finish the setup first, or switch it on anyway if you are only testing.',
      acceptLabel: 'Switch on anyway',
    };
  }
  return null;
}

/** Toast after a provider is switched on or off. */
export function providerToggleToast(name: string, isActive: boolean): { summary: string; detail: string } {
  const label = providerLabel(name);
  return isActive
    ? { summary: `${label} switched on`, detail: `Students and parents can now pay online with ${label}.` }
    : { summary: `${label} switched off`, detail: `Students and parents can no longer pay with ${label}.` };
}

// ── Form state ──

/**
 * Add/edit form for one provider: values, inline validation (vuelidate) and
 * server field errors (useFormErrors), so Payment.vue only renders it.
 */
export function usePaymentProviderForm(
  statuses: Ref<PaymentProviderStatus[]>,
  providers: Ref<Array<SavedProvider | null | undefined>>,
) {
  const name = ref('');
  const editing = ref(false);
  const values = reactive<Record<string, string>>({});
  const { handleServerErrors, clearErrors, getErrorMessage } = useFormErrors();

  const fields = computed(() => formFieldsFor(name.value));
  const savedKeys = computed(() => savedFieldKeys(statuses.value.find((status) => isCode(status.name, name.value))));
  const options = computed(() => providerOptionsFor(providers.value.map((provider) => provider?.name), editing.value));
  const allProvidersAdded = computed(() => !editing.value && options.value.every((option) => option.disabled));

  const rules = computed(() => {
    const valueRules: Record<string, ValidationArgs> = {};
    for (const field of fields.value) {
      valueRules[field.key] = {
        ...(isFieldRequired(field, savedKeys.value, editing.value)
          ? { required: helpers.withMessage(`Enter the ${field.label}.`, required) }
          : {}),
        format: helpers.withMessage(
          () => keyFormatProblem(name.value, field.key, values[field.key]),
          (value: string) => !keyFormatProblem(name.value, field.key, value),
        ),
      };
    }
    return {
      name: { required: helpers.withMessage('Choose a payment provider.', required) },
      values: valueRules,
    };
  });

  const v$ = useVuelidate(rules, { name, values });

  function setValues(source: Record<string, unknown> | null | undefined) {
    for (const key of Object.keys(values)) delete values[key];
    for (const field of fields.value) {
      const stored = source?.[field.key];
      values[field.key] = !field.secret && typeof stored === 'string' ? stored : '';
    }
  }

  function selectProvider(next: string) {
    name.value = next;
    clearErrors();
    setValues(null);
    v$.value.$reset();
  }

  function reset() {
    editing.value = false;
    selectProvider('');
  }

  function startEdit(provider: SavedProvider) {
    editing.value = true;
    name.value = provider.name ?? '';
    clearErrors();
    setValues(provider.config as Record<string, unknown> | null | undefined);
    v$.value.$reset();
  }

  function isSaved(field: ProviderFormField): boolean {
    return editing.value && savedKeys.value.includes(field.key);
  }

  function fieldError(key: string): string {
    const fieldState = (v$.value.values as Record<string, { $errors?: Array<{ $message: unknown }> }> | undefined)?.[key];
    const message = fieldState?.$errors?.[0]?.$message;
    return (typeof message === 'string' && message) || getErrorMessage(key);
  }

  const nameError = computed(() => {
    const message = v$.value.name.$errors[0]?.$message;
    return (typeof message === 'string' && message) || getErrorMessage('name');
  });

  /** Validates, then saves through `save`. Server field errors land under their fields. */
  async function submit(save: (input: PaymentProviderInput) => Promise<unknown>): Promise<boolean> {
    clearErrors();
    if (!(await v$.value.$validate())) return false;
    try {
      await save(buildProviderInput(name.value, values));
      return true;
    } catch (error) {
      handleServerErrors(error);
      return false;
    }
  }

  return {
    name,
    editing,
    values,
    fields,
    options,
    allProvidersAdded,
    v$,
    selectProvider,
    reset,
    startEdit,
    isSaved,
    fieldError,
    nameError,
    submit,
  };
}
